import type {
  AlertStatus,
  BillingInterval,
  CompanyRating,
  CompanyRegistryStatus,
  ContractStatus,
  ContractType,
  CustomerHealth,
  CustomerSegment,
  DomainRuleCategory,
  DomainRuleFrequency,
  DomainRuleSeverity,
  GeneratedBy,
  InvoiceBasis,
  InvoiceOutcome,
  InvoiceStatus,
  MilestoneStatus,
  RuleCategory,
  RunFrequency,
  Severity,
  SignalArea,
  SignalLevel,
  SyncSource,
  SyncStatus,
  Unit,
  BuiltInRole,
  UserStatus,
  ValidationStatus,
} from '../types/enums.js';
import type { TvAutoscroll, TvCalendarKind } from '../types/api.js';
import type { InvoicePaymentState } from '../calc/invoice-payment.js';
import type { PaymentTermsIssue } from '../calc/invoicing.js';
import type {
  CommissionExclusion,
  CommissionLineType,
  CommissionPayment,
  PartnerAgreementStatus,
} from '../calc/partner-commission.js';
import type { SubscriptionInterval, SubscriptionKind } from '../calc/subscription.js';

/**
 * Danske labels for enum-vaerdier.
 *
 * Ligger i shared og ikke i web, fordi baade UI og Teams-kortene i API'et skal skrive de
 * samme ord. To lister ville drive fra hinanden.
 */

/**
 * The built-in roles' names. A role an admin created has its own, in `codepass_roles` - so a
 * role's name is `RoleDefinition.label`, and this list only fills it for the four.
 */
export const USER_ROLE_LABELS: Record<BuiltInRole, string> = {
  admin: 'Admin',
  finance: 'Finance',
  sales: 'Sælger',
  developer: 'Udvikler',
};

export const USER_STATUS_LABELS: Record<UserStatus, string> = {
  Active: 'Aktiv',
  Inactive: 'Inaktiv',
};

/**
 * Danske labels for OnePro's opgavestatusser.
 *
 * Record<string, string> og ikke Record<TimeEntryStatus, string>: opslaget sker paa en
 * raa vaerdi fra OnePro, som kan indfoere en status uden at Codepass ved det.
 * timeEntryStatusLabel viser den raa vaerdi i det tilfaelde - en ukendt status skal kunne
 * ses i listen, ikke blive til en tom celle.
 */
export const TIME_ENTRY_STATUS_LABELS: Record<string, string> = {
  'Not Started': 'Ikke startet',
  'In Progress': 'I gang',
  Waiting: 'Afventer',
  QA: 'QA',
  Testing: 'Test',
  'Ready for invoicing': 'Klar til fakturering',
  'Partially Ready for Invoicing': 'Delvist klar til fakturering',
  'Not Billable': 'Ikke fakturerbar',
  Internal: 'Intern',
  Sales: 'Salg',
  Invoiced: 'Faktureret',
};

export function timeEntryStatusLabel(status: string | null | undefined): string {
  if (!status) return '—';
  return TIME_ENTRY_STATUS_LABELS[status] ?? status;
}

/**
 * Danske labels for Orchestrators aendringsanmodninger - `orchestrator.change_requests`.
 *
 * Samme forbehold som paa OnePro's statusser: vaerdierne er Orchestrators egne enums og laeses
 * raat. `Record<string, string>` og ikke Record<Enum, string>, saa en vaerdi, Orchestrator
 * tilfoejer i morgen, vises som den staar frem for at blive til en tom celle.
 */
export const CR_CHANNEL_LABELS: Record<string, string> = {
  status_meeting: 'Statusmøde',
  email: 'Email',
  teams: 'Teams',
  workshop: 'Workshop',
  phone: 'Telefon',
  other: 'Andet',
};

export const CR_CLASSIFICATION_LABELS: Record<string, string> = {
  out_of_scope: 'Uden for scope',
  in_scope_clarification: 'Præcisering i scope',
  grey_zone: 'Gråzone',
};

export const CR_PRIORITY_LABELS: Record<string, string> = {
  critical: 'Kritisk',
  high: 'Høj',
  medium: 'Mellem',
  low: 'Lav',
};

export const CR_RECOMMENDATION_LABELS: Record<string, string> = {
  approve_billable: 'Godkend — fakturerbar',
  approve_no_charge: 'Godkend — uden beregning',
  reject: 'Afvis',
  defer_phase_2: 'Udskyd til fase 2',
  covered_by_scope: 'Dækket af scope',
};

export const CR_STATUS_LABELS: Record<string, string> = {
  new: 'Ny',
  under_assessment: 'Under vurdering',
  awaiting_customer: 'Afventer kunde',
  approved: 'Godkendt',
  rejected: 'Afvist',
  deferred_phase_2: 'Udskudt til fase 2',
  implemented: 'Implementeret',
};

/** Slaar en vaerdi op i et af kataloegerne ovenfor. Ukendt vaerdi vises raat, tom bliver em-streg. */
export function crLabel(labels: Record<string, string>, value: string | null | undefined): string {
  const key = value?.trim();
  if (!key) return '—';
  return labels[key] ?? key;
}

export const CUSTOMER_SEGMENT_LABELS: Record<CustomerSegment, string> = {
  enterprise: 'Enterprise',
  mid_market: 'Mid market',
  sme: 'SMV',
  public: 'Offentlig',
};

export const CUSTOMER_HEALTH_LABELS: Record<CustomerHealth, string> = {
  healthy: 'Sund',
  payment_behaviour: 'Betalingsadfærd',
  inactive: 'Inaktiv',
};

export const COMPANY_RATING_LABELS: Record<CompanyRating, string> = {
  low: 'Lav risiko',
  medium: 'Middel risiko',
  high: 'Høj risiko',
  critical: 'Kritisk',
  unknown: 'Ukendt',
};

export const SIGNAL_LEVEL_LABELS: Record<SignalLevel, string> = {
  good: 'God',
  ok: 'Acceptabel',
  weak: 'Svag',
  bad: 'Dårlig',
};

export const SIGNAL_AREA_LABELS: Record<SignalArea, string> = {
  registry: 'CVR-registret',
  financials: 'Regnskab',
  payments: 'Betalinger',
};

export const INVOICE_OUTCOME_LABELS: Record<InvoiceOutcome, string> = {
  paid: 'Betalt',
  credited: 'Krediteret',
  open: 'Åben',
  overdue: 'Forfalden',
  unresolved: 'Lukket uden betaling',
};

export const COMPANY_REGISTRY_STATUS_LABELS: Record<CompanyRegistryStatus, string> = {
  active: 'Normal',
  distress: 'Konkursdekret',
  bankrupt: 'Konkurs',
  ceased: 'Ophørt',
};

export const CONTRACT_TYPE_LABELS: Record<ContractType, string> = {
  fixed_price: 'Fastpris',
  time_materials: 'Tid & materiale',
  recurring: 'Abonnement',
  milestone: 'Milepæl',
};

export const CONTRACT_STATUS_LABELS: Record<ContractStatus, string> = {
  draft: 'Kladde',
  awaiting_signature: 'Afventer signatur',
  active: 'Aktiv',
  completed: 'Afsluttet',
  cancelled: 'Annulleret',
  expired: 'Udløbet',
};

export const BILLING_INTERVAL_LABELS: Record<BillingInterval, string> = {
  monthly: 'Månedlig',
  quarterly: 'Kvartalsvis',
  on_milestone: 'På milepæl',
  final_invoice: 'Slutfaktura',
};

/** How often a subscription is billed. */
export const SUBSCRIPTION_INTERVAL_LABELS: Record<SubscriptionInterval, string> = {
  monthly: 'Månedlig',
  quarterly: 'Kvartalsvis',
  yearly: 'Årlig',
};

export const SUBSCRIPTION_KIND_LABELS: Record<SubscriptionKind, string> = {
  application_operations: 'Applikationsdrift',
  app: 'App',
};

export const UNIT_LABELS: Record<Unit, string> = {
  hours: 'timer',
  pcs: 'stk',
  month: 'måned',
  year: 'år',
  fixed: 'fast',
};

export const MILESTONE_STATUS_LABELS: Record<MilestoneStatus, string> = {
  planned: 'Planlagt',
  in_progress: 'I gang',
  reached: 'Nået',
  invoiced: 'Faktureret',
  cancelled: 'Annulleret',
};

export const VALIDATION_STATUS_LABELS: Record<ValidationStatus, string> = {
  pending: 'Afventer',
  approved: 'Godkendt',
  rejected: 'Afvist',
  invoiced: 'Faktureret',
};

export const INVOICE_STATUS_LABELS: Record<InvoiceStatus, string> = {
  draft: 'Kladde',
  approved: 'Godkendt',
  sent_to_bc: 'Sendt til BC',
  posted: 'Bogført',
  failed: 'Fejlet',
  cancelled: 'Annulleret',
};

/**
 * Business Centrals **egen** fakturastatus paa dansk.
 *
 * Ikke det samme som INVOICE_STATUS_LABELS ovenfor: den oversaetter Codepass' status, denne
 * oversaetter den BC svarer med. BC's API svarer altid paa engelsk - `Draft`, `Open`, `Paid` -
 * uanset hvilket sprog selskabet koerer paa, og brugervendt tekst i Codepass er dansk. Ordene
 * her er BC's egne danske, saa en controller laeser det samme paa /fakturering/:id som inde i
 * Business Central.
 *
 * Noeglerne er normaliserede (lowercase, trimmet), fordi vaerdien kommer raat fra et fremmed
 * API. Samme normalisering som statusmapningen i `apps/api/src/integrations/bc-invoices.ts` -
 * de to lister skal daekke de samme statusser, og `bc-invoices.test.ts` holder oeje med det.
 */
export const BC_INVOICE_STATUS_LABELS: Record<string, string> = {
  draft: 'Kladde',
  'in review': 'Til gennemsyn',
  open: 'Åben',
  paid: 'Betalt',
  canceled: 'Annulleret',
  cancelled: 'Annulleret',
  corrective: 'Korrigerende',
};

/**
 * BC's status paa dansk, eller null naar der ikke er nogen.
 *
 * En status vi ikke kender vises **uoversat** frem for som en tom celle: at BC siger noget vi
 * ikke har et ord for, er stadig en oplysning - og det er bedre end at skjule den.
 */
/**
 * A posted invoice's payment, under Status - see `invoicePaymentState`. Unpaid keeps the word
 * the status already has.
 */
export const INVOICE_PAYMENT_LABELS: Record<InvoicePaymentState, string> = {
  paid: 'Betalt',
  partly_paid: 'Delvist betalt',
  unpaid: 'Bogført',
};

/** Why a customer has the default payment terms - see `PaymentTermsIssue`. A whole sentence. */
export const PAYMENT_TERMS_ISSUE_LABELS: Record<PaymentTermsIssue, string> = {
  missing: 'Kunden har ingen betalingsbetingelse i Business Central',
  unknown: 'Kundens betalingsbetingelse findes ikke i Business Central',
  unreadable: 'Kundens betalingsbetingelse i Business Central kan ikke læses',
};

/** The same, short - next to "Netto 8 dage" on /kunder. */
export const PAYMENT_TERMS_ISSUE_SHORT_LABELS: Record<PaymentTermsIssue, string> = {
  missing: 'mangler i BC',
  unknown: 'findes ikke i BC',
  unreadable: 'kan ikke læses i BC',
};

/**
 * The warning on an invoice for a customer with the default payment terms - on /fakturering/ny,
 * and on /kunder/:id after an automatic draft.
 */
export function paymentTermsWarning(issue: PaymentTermsIssue, days: number): string {
  return (
    `${PAYMENT_TERMS_ISSUE_LABELS[issue]}. Forfaldsdatoen er sat til standarden, netto ${days} ` +
    'dage. Ret betalingsbetingelsen på kundekortet i Business Central.'
  );
}

/** CRM's own words for the partner agreement's status - see `PartnerAgreement`. */
export const PARTNER_AGREEMENT_STATUS_LABELS: Record<PartnerAgreementStatus, string> = {
  draft: 'Udkast',
  active: 'Aktiv',
  terminated: 'Opsagt',
  ended: 'Ophørt',
};

export const COMMISSION_LINE_TYPE_LABELS: Record<CommissionLineType, string> = {
  hours: 'Udviklingstimer',
  recurring: 'Løbende ydelser',
  other: 'Øvrige ydelser',
};

export const COMMISSION_PAYMENT_LABELS: Record<CommissionPayment, string> = {
  paid: 'Betalt',
  awaiting_payment: 'Afventer betaling',
  credited: 'Krediteret',
  partly_credited: 'Delvist krediteret',
  unresolved: 'Lukket uden betaling',
};

/** Why a line earns no commission - the hint under the em dash in the commission column. */
export const COMMISSION_EXCLUSION_LABELS: Record<CommissionExclusion, string> = {
  rate_missing: 'Sats mangler på salgsmuligheden',
  credited: 'Fakturaen er krediteret',
  partly_credited: 'Delvist krediteret — kontrollér i BC',
  unresolved: 'Lukket uden betaling i BC',
  after_end: 'Faktureret efter aftalens ophør',
};

export function bcInvoiceStatusLabel(status: string | null | undefined): string | null {
  const raw = (status ?? '').trim();
  if (raw.length === 0) return null;
  return BC_INVOICE_STATUS_LABELS[raw.toLowerCase()] ?? raw;
}

export const INVOICE_BASIS_LABELS: Record<InvoiceBasis, string> = {
  milestone: 'Milepæl',
  hours: 'Timer',
  recurring: 'Abonnement',
  fixed_price_partial: 'Delfaktura',
  final: 'Slutfaktura',
};

export const GENERATED_BY_LABELS: Record<GeneratedBy, string> = {
  agent: 'Automatisk',
  manual: 'Manuelt',
};

export const SEVERITY_LABELS: Record<Severity, string> = {
  info: 'Info',
  warning: 'Advarsel',
  critical: 'Kritisk',
};

export const ALERT_STATUS_LABELS: Record<AlertStatus, string> = {
  open: 'Åben',
  in_progress: 'Under behandling',
  resolved: 'Løst',
  snoozed: 'Udsat',
  dismissed: 'Afvist',
};

export const RULE_CATEGORY_LABELS: Record<RuleCategory, string> = {
  invoicing: 'Fakturering',
  budget: 'Budget',
  renewal: 'Fornyelse',
  hours: 'Timer',
  compliance: 'Compliance',
};

export const RUN_FREQUENCY_LABELS: Record<RunFrequency, string> = {
  hourly: 'Hver time',
  daily: 'Dagligt',
  weekly: 'Ugentligt',
};

/**
 * Labels til codepass_domain_rules. Vaerdierne er allerede danske; labels findes for at give
 * dem stort begyndelsesbogstav og laesbar form ('timelig' -> 'Hver time') uden at nogen
 * formaterer dem i haanden i en komponent.
 */
export const DOMAIN_RULE_CATEGORY_LABELS: Record<DomainRuleCategory, string> = {
  fakturering: 'Fakturering',
  budget: 'Budget',
  fornyelse: 'Fornyelse',
  timer: 'Timer',
  compliance: 'Compliance',
};

export const DOMAIN_RULE_SEVERITY_LABELS: Record<DomainRuleSeverity, string> = {
  info: 'Info',
  advarsel: 'Advarsel',
  kritisk: 'Kritisk',
};

/**
 * Frekvenserne med tidspunktet i labelen.
 *
 * Tidsplanen er faste tidspunkter paa den danske vaegur - se `slotBefore` i
 * domain-rule-runner.ts - og et valg der bare hed 'Dagligt' ville ikke sige hvornaar. Den, der
 * vaelger frekvensen paa /admin/regler, skal kunne se, at alarmerne kommer om morgenen.
 */
export const DOMAIN_RULE_FREQUENCY_LABELS: Record<DomainRuleFrequency, string> = {
  timelig: 'Hver time',
  daglig: 'Dagligt kl. 8',
  ugentlig: 'Mandag kl. 8',
};

export const SYNC_SOURCE_LABELS: Record<SyncSource, string> = {
  getaccept: 'GetAccept',
  onepro: 'OnePro',
  dynamics: 'Dynamics 365',
  bc: 'Business Central',
  bank: 'Bank',
};

export const SYNC_STATUS_LABELS: Record<SyncStatus, string> = {
  success: 'Succes',
  failed: 'Fejlet',
  partial: 'Delvis',
};

export const TV_AUTOSCROLL_LABELS: Record<TvAutoscroll, string> = {
  off: 'Fra',
  slow: 'Langsom',
  normal: 'Normal',
  fast: 'Hurtig',
};

export const TV_CALENDAR_KIND_LABELS: Record<TvCalendarKind, string> = {
  holiday: 'Fridag',
  school: 'Skole',
  course: 'Kursus',
  parental_leave: 'Barsel',
};

/** Short weekday names, Monday first - index 0 is ISO weekday 1. */
export const WEEKDAY_SHORT_LABELS = ['Man', 'Tir', 'Ons', 'Tor', 'Fre', 'Lør', 'Søn'] as const;

/** Danske labels for audit-log-handlinger. Ukendte handlinger vises som deres raa navn. */
export const AUDIT_ACTION_LABELS: Record<string, string> = {
  approve_hour: 'Godkendte time',
  reject_hour: 'Afviste time',
  revalidate_hour: 'Gen-validerede time',
  create_invoice: 'Oprettede fakturaudkast',
  approve_invoice: 'Godkendte fakturaudkast',
  unapprove_invoice: 'Fortrød godkendelse af fakturaudkast',
  cancel_invoice: 'Annullerede fakturaudkast',
  send_invoice_to_bc: 'Sendte faktura til BC',
  update_contract: 'Opdaterede kontrakt',
  mark_milestone_reached: 'Markerede milepæl som nået',
  start_alert: 'Tog alarm under behandling',
  resolve_alert: 'Løste alarm',
  snooze_alert: 'Udsatte alarm',
  dismiss_alert: 'Afviste alarm',
  comment_alert: 'Kommenterede alarm',
  run_domain_rules: 'Kørte reglerne manuelt',
  refresh_tv_dashboard: 'Opdaterede TV-skærmen',
  update_domain_rule: 'Opdaterede regel',
  delete_domain_rule: 'Slettede regel',
  seed_domain_rules: 'Oprettede standardregler',
  reveal_integration_setting: 'Viste integrationsnøgle',
  update_integration_setting: 'Opdaterede integrationsnøgle',
  delete_integration_setting: 'Slettede integrationsnøgle',
  update_tv_dashboard_settings: 'Opdaterede TV-dashboardet',
  update_tv_dashboard_goals: 'Opdaterede TV-dashboardets mål',
  update_budget: 'Satte budget på en konto',
  import_budget: 'Uploadede budget fra Excel',
  update_tv_utilization_settings: 'Opdaterede TV-dashboardets faktureringsgrad',
  create_tv_calendar_entry: 'Tilføjede dage i TV-kalenderen',
  update_tv_calendar_entry: 'Rettede dage i TV-kalenderen',
  delete_tv_calendar_entry: 'Slettede dage i TV-kalenderen',
  update_contract_buffer: 'Satte aftalens buffer',
  update_invoice_schedule: 'Ændrede kundens faktureringsplan',
  update_invoice_rules: 'Ændrede kundens fakturaregler',
  approve_partner_statement: 'Godkendte provisionsopgørelse',
  send_partner_statement: 'Sendte provisionsopgørelse til partneren',
  send_partner_statement_failed: 'Afsendelse af provisionsopgørelse fejlede',
  update_partner_commission_email: 'Ændrede mailopsætningen for partnerprovision',
  create_subscription: 'Oprettede abonnement',
  end_subscription: 'Afsluttede abonnement',
  overwrite_subscription: 'Overskrev abonnement',
  copy_settings: 'Flyttede indstillinger til codepass_settings',
  create_user: 'Oprettede bruger',
  update_user: 'Opdaterede bruger',
  update_role_access: 'Ændrede rollens rettigheder',
  create_role: 'Oprettede rolle',
  rename_role: 'Omdøbte rolle',
  delete_role: 'Slettede rolle',
  deactivate_user: 'Deaktiverede bruger',
  reactivate_user: 'Genaktiverede bruger',
  login: 'Loggede ind',
  change_password: 'Skiftede adgangskode',
  reset_password: 'Nulstillede adgangskode',
  update_own_profile: 'Opdaterede egen profil',
  update_rule: 'Opdaterede regel',
  'sync:getaccept': 'Synkroniserede GetAccept',
  'sync:onepro': 'Synkroniserede OnePro',
  'sync:dynamics': 'Synkroniserede Dynamics',
  'agent:generate_recurring': 'Genererede abonnementsfaktura',
  'agent:detection_run': 'Kørte detekteringsmotor',
  'agent:purge_audit_logs': 'Slettede gamle audit-poster',
  'agent:purge_alarms': 'Slettede gamle alarmer',
  'agent:release_invoice_hours': 'Frigav timer fra faktura slettet, annulleret eller rettet i BC',
};

export function auditActionLabel(action: string): string {
  return AUDIT_ACTION_LABELS[action] ?? action;
}
